import { CardList } from '@eventbrite/eds-card-list';
import { Vertical } from '@eventbrite/eds-layout';
import {
    ChoiceCard,
    ExpansionPanel,
    ExpansionPanelContent,
    ExpansionPanelTrigger,
    Typography,
} from '@eventbrite/marmalade';
import { $FixMe } from '@eventbrite/ts-utils';
import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import { TicketClassCategory } from '../../../constants';
import { shouldShowPromoCodePills } from '../../../containers/ticketSelection/utils/shouldShowPromoCodePills';
import { isDonationTicket } from '../../../utils/tickets';
import { TicketVariantForSelectionListProps } from '../constants';
import TicketVariantCard from '../TicketVariantCard';
import { getDonationChangeActionPayload } from '../utils';
import styles from './MultipleTicketVariantsCard.module.scss';

export interface MultipleTicketVariantsCardProps {
    category: TicketClassCategory;
    currencyFormat: string;
    id: string;
    image?: {
        url: string;
        original: {
            url: string;
        };
    };
    maximumQuantityPerOrder?: number | null;
    name: string;
    onDonationChange?: (payload: {
        variantId?: string;
        id: string;
        amount: any;
    }) => void;
    onQuantityChange: (payload: {
        variantId?: string;
        id: string;
        quantity: number;
    }) => void;
    priceRange: string;
    showJoinWaitlist: boolean;
    variants: Array<TicketVariantForSelectionListProps>;
}

const _calculateMaxQuantityAvailable = (
    maximumQuantityPerOrder: number,
    totalTicketsSelected: number,
    selectedQuantity: number,
) => maximumQuantityPerOrder - totalTicketsSelected + selectedQuantity;

const getTicketOptionsRange = ({
    variant,
    maximumQuantityPerTicketGroup,
    totalTicketsSelected,
}: {
    variant: MultipleTicketVariantsCardProps['variants'][number];
    maximumQuantityPerTicketGroup: MultipleTicketVariantsCardProps['maximumQuantityPerOrder'];
    totalTicketsSelected: number;
}) => {
    if (variant.category !== TicketClassCategory.admission) {
        return variant.ticketOptionsRange;
    }

    /**
     *  Rule 1
     *     If the variant level's variantMaxQuantity is not null
     *     then variants must respect that
     *  Rule 2
     *    If the ticket level's maximumQuantityPerTicketGroup is not null,
     *    then the sum of quantity for the variants for the ticket must
     *    also respect that in addition to variant level's variantMaxQuantity
     *  Rule 3
     *    If the ticket level's maximumQuantityPerTicketGroup is null, then we only care
     *    about the variants variantMaxQuantity
     */
    const validRangeOptionMax = maximumQuantityPerTicketGroup
        ? _calculateMaxQuantityAvailable(
              maximumQuantityPerTicketGroup,
              totalTicketsSelected,
              variant.selectedQuantity,
          )
        : // We should get the largest number from the ticket options range in order to get the max quantity
          Math.max(...variant.ticketOptionsRange);

    return variant.ticketOptionsRange.filter(
        (ticketOption) => ticketOption <= validRangeOptionMax,
    );
};

export const MultipleTicketVariantsCard: FunctionComponent<
    MultipleTicketVariantsCardProps
> = ({
    category,
    currencyFormat,
    id,
    image,
    maximumQuantityPerOrder,
    name,
    onDonationChange,
    onQuantityChange,
    priceRange,
    variants,
}) => {
    const isAddOn = category === TicketClassCategory.addOn;

    const _handleQuantityChange = (
        variantId: string | null,
        quantity: number,
    ) => {
        const payload = {
            id,
            quantity,
            ...(variantId ? { variantId } : {}),
        };

        onQuantityChange(payload);
    };

    const _handleDonationChange = (variantId: string, amount: number) => {
        onDonationChange?.(
            getDonationChangeActionPayload(
                id,
                `${amount}`,
                currencyFormat,
                variantId,
            ),
        );
    };

    const totalTicketsSelected = variants.reduce(
        (acc, singleVariant) => acc + singleVariant.selectedQuantity,
        0,
    );

    const getImageFromVariants = (variants: Array<$FixMe>) => {
        const variantWithImage = variants.find((variant) => variant.image);
        return variantWithImage ? variantWithImage.image : null;
    };

    const items = variants.map((variant) => {
        const onQuantityChange = (quantity: number) => {
            _handleQuantityChange(variant.id, quantity);
        };

        const onDonationChange = ({ amount }: { amount: number }) => {
            _handleDonationChange(variant.id, amount);
        };

        const ticketOptionsRange = getTicketOptionsRange({
            variant,
            maximumQuantityPerTicketGroup: maximumQuantityPerOrder,
            totalTicketsSelected,
        });

        const isDonation = isDonationTicket(variant);

        const imageThumbnail = variant.image
            ? variant.image
            : image
            ? image
            : getImageFromVariants(variants);

        const mappedImage = imageThumbnail
            ? {
                  ticketName: variant.name.toString(),
                  fullSizeUrl: imageThumbnail?.original.url,
                  thumbnailUrl: imageThumbnail?.url,
              }
            : undefined;

        const { amountOff, discountType, onSaleStatus } = variant;
        const { showDiscountedPill, showUnlockedPill } =
            shouldShowPromoCodePills({ amountOff, discountType, onSaleStatus });

        return {
            id: variant.id,
            content: (
                <TicketVariantCard
                    category={variant.category}
                    cost={variant.cost}
                    currencyFormat={currencyFormat}
                    currencySymbol={variant.currencySymbol ?? ''}
                    description={variant.description}
                    donationAmount={variant.donationAmount}
                    feesAndTaxes={variant.feeAndTax ?? []}
                    formattedSalesEndDate={variant.salesEnd ?? ''}
                    formattedSalesStartDate={variant.salesStart ?? ''}
                    hideSaleDates={variant.hideSaleDates}
                    hideSelect={variant.hideSelect}
                    image={mappedImage}
                    includeFee={variant.includeFee}
                    includeTax={variant.includeTax}
                    isBOGOTicket={false}
                    isCurrencySuffix={false}
                    isDonation={isDonation}
                    name={variant.name}
                    onDonationChange={onDonationChange}
                    onQuantityChange={onQuantityChange}
                    price={variant.displayPrice ?? null}
                    priceWithoutDiscounts={variant.primaryPrice ?? ''}
                    remainingQuantity={variant.quantityRemaining}
                    selectableQuantities={ticketOptionsRange}
                    selectedQuantity={variant.selectedQuantity}
                    showDiscountedPill={showDiscountedPill}
                    showJoinWaitlist={variant.showJoinWaitlist}
                    showUnlockedPill={showUnlockedPill}
                    ticketId={variant.id}
                    ticketStatus={variant.statusKey}
                    urgencySignals={null}
                />
            ),
        };
    });

    const itemsList = <CardList items={items} />;

    const addOnName = (
        <Typography variant="body-lg" color="neutral-800">
            {name}
        </Typography>
    );

    const sectionImage = image ? image : getImageFromVariants(variants);

    const cardContent = isAddOn ? (
        <>
            <Vertical className="eds-l-pad-all-4">{addOnName}</Vertical>
            {itemsList}
        </>
    ) : (
        <ExpansionPanel
            defaultOpen
            variant="ghost"
            className={styles.expansionPanel}
        >
            <ExpansionPanelTrigger className={styles.expansionPanelTrigger}>
                <Vertical className="eds-l-pad-hor-4">
                    {addOnName}
                    {sectionImage}
                    <Typography variant="body-md" color="neutral-600">
                        {priceRange}
                    </Typography>
                </Vertical>
            </ExpansionPanelTrigger>
            <ExpansionPanelContent>{itemsList}</ExpansionPanelContent>
        </ExpansionPanel>
    );

    return (
        <ChoiceCard
            className={classNames(styles.card, 'eds-l-mar-top-4')}
            data-testid="multiple-ticket-variants-card"
        >
            {cardContent}
        </ChoiceCard>
    );
};
